import {
  clearClubImportReturnTo,
  getClubImportReturnTo,
} from "@/features/MyClub/EmptyClub/clubImportReturn";
import {
  raiseClubImportOverlay,
  useLaunchClubImport,
} from "@/features/MyClub/EmptyClub/useStartClubImport";
import { getSafariVersion } from "@/lib/safariVersion";
import {
  checkExtensionAvailability,
  selectClubImportFirstPartComplete,
  selectClubImportState,
  selectExtensionCheckReady,
  selectIsDesktopChrome,
  setIosExtensionInstalled,
} from "@/redux/clubImport/clubImportSlice";
import { globalStateSelector } from "@/redux/globalState/globalStateSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS, chromeExtensionUrl } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useRef, useState } from "react";
import { isIOS } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { FirstPartSection } from "./DesktopComponents/FirstPartSection";
import { SecondPartSection } from "./DesktopComponents/SecondPartSection";
import { StillStrugglingSection } from "./DesktopComponents/StillStrugglingSection";
import { IosOrDesktopRequired } from "./MobileComponents/IosOrDesktopRequired";
import { MainSection } from "./MobileComponents/MainSection";
import { SafariUpdateRequired } from "./MobileComponents/SafariUpdateRequired";
import { useClubImportFunnel } from "./useClubImportFunnel";
import { useExtensionInstallPrompt } from "./useExtensionInstallPrompt";

// Club import on mobile requires Safari 15.4+ (extension compatibility).
const isSafariBelowClubImportMinimum = (): boolean => {
  const v = getSafariVersion();
  if (v === null) return false;
  if (v.major < 15) return true;
  if (v.major > 15) return false;
  return v.minor < 4;
};

export const ClubImportTutorial = () => {
  const { t } = useTranslation("clubImport");
  const { browser, extensionEnabledStatus, iosExtensionInstalled } =
    useSelector(selectClubImportState);
  const { isCurrentlyImporting, lastImportFinishedAt } =
    useSelector(globalStateSelector);
  const { user } = useSelector(getUserSelector);
  const extensionCheckReady = useSelector(selectExtensionCheckReady);
  const firstPartComplete = useSelector(selectClubImportFirstPartComplete);
  const isDesktopChrome = useSelector(selectIsDesktopChrome);
  const loggedIn = !!user.uuid;

  const [isFirstPartCollapsed, setIsFirstPartCollapsed] =
    useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  // iPads are wider than the phone breakpoint but import the way iPhones do,
  // through the iOS app's Safari extension, so iOS always gets that flow. The
  // desktop checklist needs desktop Chrome and could never complete there.
  const isTouchDevice = useMediaQuery(BREAKPOINTS.MOBILE.query) || isIOS;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");

  // Auto-collapse once the checklist is done — the section has nothing left to
  // tell the user, and the Second part is where they act next.
  useEffect(() => {
    if (firstPartComplete) setIsFirstPartCollapsed(true);
  }, [firstPartComplete]);

  useEffect(() => {
    if (from === "extension-app") dispatch(setIosExtensionInstalled(true));
  }, [from, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // The two run different checklists, so they are separate subsections rather
  // than one funnel with a device prop.
  const subsection = isTouchDevice ? "Mobile" : "Desktop";
  useClubImportFunnel(subsection, [
    {
      value: "browser",
      completed: isTouchDevice
        ? browser === "iosSafari"
        : browser === "desktopChrome",
    },
    { value: "login", completed: loggedIn },
    ...(isTouchDevice
      ? [{ value: "ios-app" as const, completed: iosExtensionInstalled }]
      : []),
    {
      value: "extension",
      completed: extensionEnabledStatus === "enabled",
    },
  ]);

  // This page is the destination other CTAs route to, so it is its own origin
  // for the import_club event useLaunchClubImport fires.
  const launchClubImport = useLaunchClubImport({
    section: "Club Import",
    subsection,
  });

  /*
    Chrome can install an extension switched off, and a disabled extension is
    indistinguishable from a missing one from here — so the click is what we
    remember, and the prompt asks rather than tells.

    Desktop Chrome only. Anything wider than the touch breakpoint lands on this
    checklist, tablets included, and chrome://extensions is not where an Android
    or iOS user enables anything.
  */
  const { markInstallStarted, showInstallPrompt } = useExtensionInstallPrompt({
    eligible: isDesktopChrome,
    installed: extensionEnabledStatus === "enabled",
  });

  const handleLogin = () => {
    dispatch(showModal({ modalType: "login-required" }));
  };

  const handleGetChromeExtension = () => {
    markInstallStarted();
    window.open(chromeExtensionUrl("import_tutorial"), "_blank");
  };

  /*
    useLaunchClubImport, not useStartClubImport: this page *is* where the rule
    routes people, so applying it here would send a first-timer from the
    tutorial to the tutorial. The overlay then owns the wait and the Done.
  */
  const handleStartImport = async () => {
    // import_club is fired inside the launch, and only once the EA app is
    // actually open — a blocked popup or a failed init is not an import.
    if (!(await launchClubImport())) return;
    raiseClubImportOverlay(dispatch, subsection);
  };

  // The overlay reports a finished import through the store, so it is a
  // timestamp that changed — compared against mount, since an earlier import in
  // the session leaves a non-zero one behind.
  const importFinishedAtMount = useRef(lastImportFinishedAt);
  // Read at mount and cleared on unmount, not taken at mount: the login a
  // first-timer does here can reload the page, and the path must survive that.
  const returnTo = useRef(getClubImportReturnTo());
  useEffect(() => clearClubImportReturnTo, []);
  useEffect(() => {
    if (lastImportFinishedAt !== importFinishedAtMount.current)
      navigate(returnTo.current ?? "/my-club");
  }, [lastImportFinishedAt, navigate]);

  useEffect(() => {
    if (!extensionCheckReady || isCurrentlyImporting) return;
    dispatch(checkExtensionAvailability({ user }));
    const interval = setInterval(
      () => dispatch(checkExtensionAvailability({ user })),
      1000,
    );
    return () => clearInterval(interval);
  }, [extensionCheckReady, isCurrentlyImporting, user, dispatch]);

  const safariBelowMinimum = isSafariBelowClubImportMinimum();

  return isTouchDevice ? (
    !isIOS ? (
      <IosOrDesktopRequired />
    ) : safariBelowMinimum ? (
      <SafariUpdateRequired />
    ) : window.location.pathname.includes("mobile-setup-extension") ||
      window.location.pathname.includes("mobile-club-import-guide") ? (
      <Outlet />
    ) : (
      <main className="max-w-content mx-auto large:px-16">
        <MainSection
          step1Completed={browser === "iosSafari"}
          step2Completed={loggedIn}
          step3Completed={iosExtensionInstalled}
          step4Completed={extensionEnabledStatus === "enabled"}
          loggedIn={loggedIn}
          onSetupExtensionClick={() =>
            navigate("/club-import-tutorial/mobile-setup-extension")
          }
          onClubImportGuideClick={() =>
            navigate("/club-import-tutorial/mobile-club-import-guide")
          }
          iosExtensionInstalled={iosExtensionInstalled}
        />
      </main>
    )
  ) : (
    <div className="bg-panel-base min-h-screen">
      {/* Hero Section with EA FC → EasySBC visual flow */}
      <section className="w-full h-[250px] bg-[#14191F] relative overflow-hidden">
        {/* <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-light-blue/10 to-transparent" /> */}
        <div className="max-w-content mx-auto h-full relative px-4">
          <img
            className="w-full h-full object-cover"
            src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/import-tutorial/desktop/hero.png`}
            alt=""
          />
          <div className="absolute inset-y-0 left-20 z-10 flex items-center">
            <div className="flex flex-col gap-[10px] items-start">
              <div className="flex flex-col gap-4 items-start">
                <p className="font-medium leading-[24px] text-[30px] text-white tracking-[-0.3px]">
                  {t("tutorial.hero.title")}
                </p>
                <p className="leading-[normal] text-base text-dark-secondary">
                  {t("tutorial.hero.subtitle")}
                </p>
              </div>
              {/* <p className="font-['Rethink Sans',sans-serif] font-medium leading-4 text-[12px] text-dark-tertiary w-[298px]">
                Follow the steps below so we can load your players and unlock
                best evolutions, optimize SBC solutions, and squad builds that
                fit your club perfectly.
              </p> */}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-content mx-auto px-4 touch-device:px-4 large:px-16 py-8">
        {/* First Part Section */}
        <FirstPartSection
          step1Completed={browser === "desktopChrome"}
          step2Completed={loggedIn}
          step3Completed={extensionEnabledStatus === "enabled"}
          showInstallPrompt={showInstallPrompt}
          isFirstPartCollapsed={isFirstPartCollapsed}
          setIsFirstPartCollapsed={setIsFirstPartCollapsed}
          isLoggedIn={loggedIn}
          onLogin={handleLogin}
          handleGetChromeExtension={() => handleGetChromeExtension()}
        />

        {/* Second Part Section */}
        <SecondPartSection
          canStartImport={firstPartComplete}
          onStartImport={handleStartImport}
        />

        {/* Still Struggling Section */}
        <StillStrugglingSection />
      </main>
    </div>
  );
};
